import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

import {
  Work as WorkIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationUtils } from '../utils/navigationUtils';
import { useAuth } from '../services/AuthService';
import ApplicationService from '../services/ApplicationService';
import JobService from '../services/JobService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import FeedbackDialog from '../components/FeedbackDialog';
import FeedbackService from '../services/FeedbackService';
import { formatDate } from '../utils/dateUtils';


const MyApplications = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { navigateBackToDashboard } = useNavigationUtils();
  const [applications, setApplications] = useState([]);
  const [jobDetails, setJobDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setJobSeekerProfile] = useState(null);
  const [jobSeekerProfileForFeedback, setJobSeekerProfileForFeedback] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null); // { ratedProfileId, ratedName, jobId, applicationId }
  const [submittedFeedbacks, setSubmittedFeedbacks] = useState(new Set()); // Set of applicationIds with feedback
  const [expandedMessages, setExpandedMessages] = useState({});
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const applicationService = new ApplicationService();
  const jobService = new JobService();
  const jobSeekerService = new JobSeekerProfileService(user);

  const summaryMetrics = [
    {
      label: 'Total Applications',
      value: applications.length,
      helper: 'Jobs you have applied to',
      color: 'primary.main'
    }
    // {
    //   label: 'Pending Review',
    //   value: statusCounts.pending,
    //   helper: 'Awaiting employer response',
    //   color: 'warning.main'
    // },
    // {
    //   label: 'Reviewed',
    //   value: statusCounts.reviewed,
    //   helper: 'Employers viewed your profile',
    //   color: 'info.main'
    // },
    // {
    //   label: 'Accepted',
    //   value: statusCounts.accepted,
    //   helper: 'Successful matches so far',
    //   color: 'success.main'
    // }
  ];

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !user) {
        setError('Please log in to view your applications.');
        setLoading(false);
        return;
      }
      
      // Get current user's job seeker profile
      const userId = user.sub;
      const userJobSeekerProfile = await jobSeekerService.getRecordsByUserId(userId);
      
      if (!userJobSeekerProfile) {
        setError('Please complete your job seeker profile to view applications.');
        setLoading(false);
        return;
      }
      
      setJobSeekerProfile(userJobSeekerProfile);
      setJobSeekerProfileForFeedback(userJobSeekerProfile);
      
      // Fetch user's applications using the job seeker profile ID
      const userApplications = await applicationService.getApplicationsByJobSeekerProfileId(userJobSeekerProfile.id);
      
      // Sort applications by applied date (newest first)
      const sortedApplications = userApplications.sort((a, b) => 
        new Date(b.appliedAt) - new Date(a.appliedAt)
      );
      
      setApplications(sortedApplications);
      
      // Fetch job details for each application
      const jobDetailsMap = {};
      for (const application of sortedApplications) {
        try {
          const job = await jobService.getJobById(application.jobId);
          jobDetailsMap[application.jobId] = job;
        } catch (jobError) {
          jobDetailsMap[application.jobId] = {
            title: 'Job not found',
            company: 'Unknown',
            location: 'Unknown'
          };
        }
      }
      
      setJobDetails(jobDetailsMap);

      // Fetch existing feedbacks to know which applications already have one
      try {
        const myFeedbacks = await FeedbackService.getMyFeedback();
        const feedbackAppIds = new Set(
          (myFeedbacks || []).map((f) => f.applicationId).filter(Boolean),
        );
        setSubmittedFeedbacks(feedbackAppIds);
      } catch {
        // non-critical
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load your applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigateBackToDashboard();
  };

  const toggleMessageExpansion = (applicationId) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [applicationId]: !prev[applicationId]
    }));
  };

  const handleViewJobDetails = (jobId) => {
    const job = jobDetails[jobId] || null;
    setSelectedJob(job);
    setJobDialogOpen(true);
  };

  const handleCloseJobDialog = () => {
    setJobDialogOpen(false);
    setSelectedJob(null);
  };

  const renderApplicationCard = (application) => {
    const job = jobDetails[application.jobId] || {};
    const companyName = job.onBehalfOfCompanyName || job.company || 'Company not specified';
    const jobRef = job.referenceCode || application.jobId;
    // origin-based UI removed; we no longer render origin-specific badges
    const appliedDateTime = formatDate(application.appliedAt, { format: 'datetime' });
    const hasMessage = Boolean(application.applicationMessage);
    const isMessageExpanded = !!expandedMessages[application.id];
    const shouldTruncateMessage = hasMessage && application.applicationMessage.length > 220;

    return (
      <Paper
        key={application.id}
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.04)',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 18px 40px rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        {/* Top row: Reference + Applied date */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
          {/* Main info */}
          <Box flex={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={600} color="text.primary">
                {jobRef}
              </Typography>
              {/* Origin badge removed */}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {companyName}
              </Typography>
            </Stack>

            {hasMessage && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'action.hover'
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                  Your Message
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    whiteSpace: 'pre-wrap',
                    display: isMessageExpanded ? 'block' : '-webkit-box',
                    WebkitLineClamp: isMessageExpanded ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {application.applicationMessage}
                </Typography>
                {shouldTruncateMessage && (
                  <Button
                    size="small"
                    onClick={() => toggleMessageExpansion(application.id)}
                    sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: 'none' }}
                  >
                    {isMessageExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Actions */}
          <Stack spacing={1.5} minWidth={{ md: 180 }} justifyContent="center">
            <Typography variant="caption" color="text.secondary">
              Applied {appliedDateTime}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleViewJobDetails(application.jobId)}
              sx={{ alignSelf: { xs: 'flex-start', md: 'stretch' } }}
            >
              View Job Details
            </Button>
            {job?.companyProfile && (
              submittedFeedbacks.has(application.id) ? (
                <Button
                  variant="outlined"
                  size="small"
                  disabled
                  startIcon={<StarIcon />}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'stretch' } }}
                >
                  Feedback Submitted
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<StarIcon />}
                  onClick={() => setFeedbackTarget({
                    ratedProfileId: job.companyProfile,
                    ratedName: companyName,
                    jobId: application.jobId,
                    applicationId: application.id,
                  })}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'stretch' } }}
                >
                  Leave Feedback
                </Button>
              )
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToDashboard}
          sx={{ mb: 2, alignSelf: 'flex-start' }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h6" color="text.secondary">
          Track your job applications
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Applications Summary */}
      {applications.length > 0 && (
        <Stack spacing={3} mb={4}>
          <Grid container spacing={2}>
            {summaryMetrics.map((metric) => (
              <Grid item xs={12} sm={6} md={3} key={metric.label}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {metric.label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: metric.color }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {metric.helper}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {/* Applications List */}
      {applications.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <WorkIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Applications Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            You haven't applied to any jobs yet. Start browsing opportunities!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/jobs')}
          >
            Browse Jobs
          </Button>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {applications.map((application) => renderApplicationCard(application))}
        </Stack>
      )}

      <Dialog
        open={jobDialogOpen}
        onClose={handleCloseJobDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedJob?.enrichedData?.job_subcategory || selectedJob?.referenceCode || selectedJob?.description || 'Job Details'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(selectedJob?.onBehalfOfCompanyName || selectedJob?.company) && (
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedJob.onBehalfOfCompanyName || selectedJob.company}
              </Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Job Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {selectedJob?.description || 'No description available for this position.'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJobDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackDialog
        open={!!feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
        ratedProfileType="company"
        ratedProfileId={feedbackTarget?.ratedProfileId}
        ratedName={feedbackTarget?.ratedName}
        raterProfileType="jobseeker"
        raterProfileId={jobSeekerProfileForFeedback?.id}
        raterName={`${jobSeekerProfileForFeedback?.basicProfile?.firstName || ''} ${jobSeekerProfileForFeedback?.basicProfile?.lastName || ''}`.trim()}
        jobId={feedbackTarget?.jobId}
        applicationId={feedbackTarget?.applicationId}
        onSubmitted={() => {
          if (feedbackTarget?.applicationId) {
            setSubmittedFeedbacks((prev) => new Set(prev).add(feedbackTarget.applicationId));
          }
          setFeedbackTarget(null);
        }}
      />
    </Container>
  );
};

export default MyApplications;